import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SendIcon, CheckCircleIcon, XIcon, UserIcon, BuildingIcon, CopyIcon, ArrowLeftIcon, Loader2Icon, CheckIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
const RequestToPayPage = () => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('iban');
  const [formData, setFormData] = useState({
    iban: '',
    alias: '',
    billNumber: '',
    amount: '',
    reference: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [transactionId, setTransactionId] = useState('');
  const validateIBAN = (iban: string) => {
    return iban.length === 24;
  };
  const validateAlias = (alias: string) => {
    return /^03\d{9}$/.test(alias);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handlePaymentMethodChange = (method: string) => {
    setPaymentMethod(method);
    setErrors({
      ...errors,
      iban: '',
      alias: ''
    });
  };
  const handleValidatePayee = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (paymentMethod === 'iban') {
      if (!validateIBAN(formData.iban)) {
        newErrors.iban = 'Please enter a valid 24-digit IBAN';
      }
    } else {
      if (!validateAlias(formData.alias)) {
        newErrors.alias = 'Please enter a valid 11-digit mobile number starting with 03';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    // Simulate API validation call
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsLoading(false);
    setStep(2);
  };
  const handleProceedToBillDetails = () => {
    setStep(3);
  };
  const handleReviewDetails = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!formData.billNumber) {
      newErrors.billNumber = 'Bill number is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount greater than 0';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setStep(4);
  };
  const handleConfirmRequest = () => {
    const newTxnId = 'TXN' + Math.random().toString(36).substring(2, 10).toUpperCase();
    setTransactionId(newTxnId);
    setStep(5);
  };
  const handleReset = () => {
    setFormData({
      iban: '',
      alias: '',
      billNumber: '',
      amount: '',
      reference: ''
    });
    setPaymentMethod('iban');
    setStep(1);
    setCopied(false);
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(transactionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <DashboardLayout data-id="element-1737">
      <div className="mb-6" data-id="element-1738">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1739">Request to Pay</h1>
        <p className="text-gray-600" data-id="element-1740">Send a payment request to a customer</p>
      </div>

      <div className="max-w-2xl mx-auto" data-id="element-1741">
        {/* Step Indicator */}
        {step < 5 && <div className="mb-8" data-id="element-1742">
            <div className="flex items-center justify-between relative" data-id="element-1743">
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-200 z-0" data-id="element-1744"></div>
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-blue-600 z-0 transition-all duration-300" style={{
            width: `${(step - 1) / 3 * 100}%`
          }} data-id="element-1745"></div>

              {[1, 2, 3, 4].map(i => <div key={i} className="relative z-10 flex flex-col items-center" data-id="element-1746">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${step >= i ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1747">
                    {step > i ? <CheckIcon className="w-5 h-5" data-id="element-1748" /> : i}
                  </div>
                  <span className={`text-xs mt-2 absolute -bottom-6 w-24 text-center ${step >= i ? 'text-blue-600 font-medium' : 'text-gray-500'}`} data-id="element-1749">
                    {i === 1 && 'Payee'}
                    {i === 2 && 'Verify'}
                    {i === 3 && 'Details'}
                    {i === 4 && 'Confirm'}
                  </span>
                </div>)}
            </div>
          </div>}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 mt-12" data-id="element-1750">
          {/* STEP 1: Enter Payee Details */}
          {step === 1 && <form onSubmit={handleValidatePayee} data-id="element-1751">
              <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1752">
                Enter Payee Details
              </h2>
              <div className="space-y-6" data-id="element-1753">
                <div data-id="element-1754">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1755">
                    Payment Method
                  </label>
                  <div className="flex space-x-6" data-id="element-1756">
                    <label className="flex items-center cursor-pointer" data-id="element-1757">
                      <input type="radio" name="paymentMethod" checked={paymentMethod === 'iban'} onChange={() => handlePaymentMethodChange('iban')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1758" />
                      <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-1759">
                        Payee IBAN
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer" data-id="element-1760">
                      <input type="radio" name="paymentMethod" checked={paymentMethod === 'alias'} onChange={() => handlePaymentMethodChange('alias')} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-1761" />
                      <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-1762">
                        Payee ALIAS
                      </span>
                    </label>
                  </div>
                </div>

                {paymentMethod === 'iban' ? <div data-id="element-1763">
                    <label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1764">
                      Payee IBAN
                    </label>
                    <input type="text" id="iban" name="iban" value={formData.iban} onChange={handleChange} className={`w-full p-3 border ${errors.iban ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} rounded-lg focus:outline-none focus:ring-2`} placeholder="Enter 24-digit IBAN" maxLength={24} data-id="element-1765" />
                    {errors.iban && <p className="mt-1 text-sm text-red-600" data-id="element-1766">{errors.iban}</p>}
                  </div> : <div data-id="element-1767">
                    <label htmlFor="alias" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1768">
                      Payee ALIAS (Mobile Number)
                    </label>
                    <input type="text" id="alias" name="alias" value={formData.alias} onChange={handleChange} className={`w-full p-3 border ${errors.alias ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} rounded-lg focus:outline-none focus:ring-2`} placeholder="03XX XXXXXXX" maxLength={11} data-id="element-1769" />
                    {errors.alias && <p className="mt-1 text-sm text-red-600" data-id="element-1770">
                        {errors.alias}
                      </p>}
                  </div>}

                <div className="pt-4" data-id="element-1771">
                  <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70" data-id="element-1772">
                    {isLoading ? <>
                        <Loader2Icon className="animate-spin h-5 w-5 mr-2" data-id="element-1773" />
                        Validating...
                      </> : 'Validate'}
                  </button>
                </div>
              </div>
            </form>}

          {/* STEP 2: Customer Verified */}
          {step === 2 && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1774">
              <div className="flex items-center justify-between mb-2" data-id="element-1775">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1776">
                  Customer Verified
                </h2>
                <button onClick={() => setStep(1)} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-1777">
                  <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1778" /> Change Payee
                </button>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6 relative overflow-hidden" data-id="element-1779">
                <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-1780">
                  <CheckCircleIcon className="w-24 h-24 text-green-600" data-id="element-1781" />
                </div>

                <div className="flex items-center mb-6 relative z-10" data-id="element-1782">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4" data-id="element-1783">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-1784" />
                  </div>
                  <div data-id="element-1785">
                    <h3 className="text-lg font-bold text-green-900" data-id="element-1786">
                      Verification Successful
                    </h3>
                    <p className="text-sm text-green-700" data-id="element-1787">
                      Account details retrieved
                    </p>
                  </div>
                </div>

                <div className="space-y-4 relative z-10 bg-white/60 p-4 rounded-lg border border-green-100" data-id="element-1788">
                  <div className="flex items-start" data-id="element-1789">
                    <UserIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5" data-id="element-1790" />
                    <div data-id="element-1791">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1792">
                        Account Title
                      </p>
                      <p className="text-base font-semibold text-gray-900" data-id="element-1793">
                        Muhammad Ahmed Khan
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1794">
                    <BuildingIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5" data-id="element-1795" />
                    <div data-id="element-1796">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1797">
                        Bank Name
                      </p>
                      <p className="text-base font-medium text-gray-900" data-id="element-1798">
                        Habib Bank Limited
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1799">
                    <div className="w-5 h-5 flex items-center justify-center text-gray-400 mr-3 mt-0.5 font-bold text-sm" data-id="element-1800">
                      #
                    </div>
                    <div data-id="element-1801">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1802">
                        {paymentMethod === 'iban' ? 'IBAN' : 'ALIAS'}
                      </p>
                      <p className="text-base font-mono text-gray-900" data-id="element-1803">
                        {paymentMethod === 'iban' ? formData.iban : formData.alias}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4" data-id="element-1804">
                <button onClick={handleProceedToBillDetails} className="w-full flex justify-center items-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1805">
                  Continue
                </button>
              </div>
            </div>}

          {/* STEP 3: Bill Details */}
          {step === 3 && <form onSubmit={handleReviewDetails} className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1806">
              <div className="flex items-center justify-between mb-6" data-id="element-1807">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1808">
                  Bill Details
                </h2>
                <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center" data-id="element-1809">
                  <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1810" /> Back
                </button>
              </div>

              <div className="space-y-5" data-id="element-1811">
                <div data-id="element-1812">
                  <label htmlFor="billNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1813">
                    Bill Number <span className="text-red-500" data-id="element-1814">*</span>
                  </label>
                  <input type="text" id="billNumber" name="billNumber" value={formData.billNumber} onChange={handleChange} className={`w-full p-3 border ${errors.billNumber ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} rounded-lg focus:outline-none focus:ring-2`} placeholder="Enter bill number" data-id="element-1815" />
                  {errors.billNumber && <p className="mt-1 text-sm text-red-600" data-id="element-1816">
                      {errors.billNumber}
                    </p>}
                </div>

                <div data-id="element-1817">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1818">
                    Amount (PKR) <span className="text-red-500" data-id="element-1819">*</span>
                  </label>
                  <div className="relative" data-id="element-1820">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1821">
                      <span className="text-gray-500 sm:text-sm" data-id="element-1822">₨</span>
                    </div>
                    <input type="number" id="amount" name="amount" value={formData.amount} onChange={handleChange} className={`w-full pl-8 p-3 border ${errors.amount ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} rounded-lg focus:outline-none focus:ring-2`} placeholder="0.00" step="0.01" data-id="element-1823" />
                  </div>
                  {errors.amount && <p className="mt-1 text-sm text-red-600" data-id="element-1824">{errors.amount}</p>}
                </div>

                <div data-id="element-1825">
                  <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1826">
                    Reference Number{' '}
                    <span className="text-gray-400 font-normal" data-id="element-1827">
                      (Optional)
                    </span>
                  </label>
                  <input type="text" id="reference" name="reference" value={formData.reference} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter reference number" data-id="element-1828" />
                </div>

                <div className="pt-6" data-id="element-1829">
                  <button type="submit" className="w-full flex justify-center items-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1830">
                    Proceed to Review
                  </button>
                </div>
              </div>
            </form>}

          {/* STEP 4: Confirmation */}
          {step === 4 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1831">
              <div className="flex items-center justify-between mb-6" data-id="element-1832">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1833">
                  Confirm Details
                </h2>
                <button onClick={() => setStep(3)} className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center" data-id="element-1834">
                  <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1835" /> Edit Details
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-8" data-id="element-1836">
                <dl className="space-y-4 text-sm" data-id="element-1837">
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1838">
                    <dt className="text-gray-500 font-medium" data-id="element-1839">Account Title</dt>
                    <dd className="text-gray-900 font-semibold text-right" data-id="element-1840">
                      Muhammad Ahmed Khan
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1841">
                    <dt className="text-gray-500 font-medium" data-id="element-1842">Bank Name</dt>
                    <dd className="text-gray-900 font-medium text-right" data-id="element-1843">
                      Habib Bank Limited
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1844">
                    <dt className="text-gray-500 font-medium" data-id="element-1845">
                      {paymentMethod === 'iban' ? 'IBAN' : 'ALIAS'}
                    </dt>
                    <dd className="text-gray-900 font-mono text-right" data-id="element-1846">
                      {paymentMethod === 'iban' ? formData.iban : formData.alias}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1847">
                    <dt className="text-gray-500 font-medium" data-id="element-1848">Bill Number</dt>
                    <dd className="text-gray-900 font-medium text-right" data-id="element-1849">
                      {formData.billNumber}
                    </dd>
                  </div>
                  {formData.reference && <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1850">
                      <dt className="text-gray-500 font-medium" data-id="element-1851">Reference</dt>
                      <dd className="text-gray-900 font-medium text-right" data-id="element-1852">
                        {formData.reference}
                      </dd>
                    </div>}
                  <div className="flex justify-between py-3 mt-2 bg-blue-50 -mx-6 px-6 border-y border-blue-100" data-id="element-1853">
                    <dt className="text-blue-900 font-bold text-base" data-id="element-1854">
                      Total Amount
                    </dt>
                    <dd className="text-blue-700 font-bold text-lg text-right" data-id="element-1855">
                      ₨{' '}
                      {parseFloat(formData.amount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="flex space-x-4" data-id="element-1856">
                <button onClick={handleConfirmRequest} className="flex-1 flex justify-center items-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm" data-id="element-1857">
                  <SendIcon className="h-4 w-4 mr-2" data-id="element-1858" />
                  Confirm & Send Request
                </button>
              </div>
            </div>}

          {/* STEP 5: Success */}
          {step === 5 && <div className="text-center py-8 animate-in zoom-in duration-500" data-id="element-1859">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1860">
                <CheckCircleIcon className="h-10 w-10 text-green-600" data-id="element-1861" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1862">
                Request Sent Successfully!
              </h2>
              <p className="text-gray-600 mb-8 max-w-sm mx-auto" data-id="element-1863">
                Your payment request has been securely transmitted to the
                customer.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 max-w-md mx-auto mb-8 text-left" data-id="element-1864">
                <p className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wider" data-id="element-1865">
                  Transaction ID
                </p>
                <div className="flex items-center justify-between bg-white border border-gray-300 rounded-lg p-3" data-id="element-1866">
                  <span className="font-mono text-gray-900 font-medium text-lg tracking-wide" data-id="element-1867">
                    {transactionId}
                  </span>
                  <button onClick={copyToClipboard} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors flex items-center" title="Copy Transaction ID" data-id="element-1868">
                    {copied ? <CheckIcon className="h-5 w-5 text-green-600" data-id="element-1869" /> : <CopyIcon className="h-5 w-5" data-id="element-1870" />}
                  </button>
                </div>
                {copied && <p className="text-xs text-green-600 mt-2 text-right" data-id="element-1871">
                    Copied to clipboard!
                  </p>}
              </div>

              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 max-w-md mx-auto" data-id="element-1872">
                <button onClick={handleReset} className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1873">
                  Create New Request
                </button>
                <Link to="/rtp-lists" className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-center" data-id="element-1874">
                  View All Requests
                </Link>
              </div>
            </div>}
        </div>
      </div>
    </DashboardLayout>;
};
export default RequestToPayPage;